import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, ArrowRight, ArrowLeft as ArrowLeftIcon, RotateCw, Hand } from 'lucide-react';
import './GuidePage.css';

export default function TrafficRulesGuide() {
  return (
    <div className="guide-page">
      <Link to="/wissen" className="back-link"><ArrowLeft size={20} /> Zurück zum Wissen</Link>
      
      <article className="guide-content">
        <h1>🚦 Verkehrsregeln Großbritannien</h1>
        
        <div className="warning-box">
          <AlertTriangle size={24} />
          <div>
            <strong>LINKSVERKEHR!</strong> Die größte Umstellung für Kontinentaleuropäer. 
            Lies diesen Guide aufmerksam - es kann Leben retten!
          </div>
        </div>

        <section>
          <h2>1. Linksverkehr - Die Basics</h2>
          
          <h3>Was bedeutet Linksverkehr?</h3>
          <div className="info-box">
            <h4>👈 Grundregeln</h4>
            <ul>
              <li><strong>Fahren:</strong> Immer links halten</li>
              <li><strong>Überholen:</strong> Rechts überholen (nicht links!)</li>
              <li><strong>Abbiegen:</strong> Bei Linksabbiegen die Straße kreuzen</li>
              <li><strong>Blickrichtung:</strong> Erst nach RECHTS schauen (dann links)</li>
            </ul>
          </div>

          <h3>Die 5 häufigsten Fehler</h3>
          <ol>
            <li><strong>Nach Kreuzung auf rechte Seite fahren</strong> (FALSCH! Links bleiben!)</li>
            <li><strong>Links überholen</strong> (In UK rechts überholen!)</li>
            <li><strong>Erst links schauen</strong> (In UK kommt Verkehr von RECHTS!)</li>
            <li><strong>Im Kreisverkehr falsch herum</strong> (Im Uhrzeigersinn!)</li>
            <li><strong>Auf leeren Straßen rechts fahren</strong> (Gewohnheit! Immer links!)</li>
          </ol>

          <div className="pro-tip">
            <h3>💡 Mental-Trick für Linksverkehr</h3>
            <p>
              <strong>"Follow the flow"</strong> - Fahre immer mit dem Verkehr mit. 
              In den ersten Tagen: Folge anderen Radfahrern/Autos, bis es automatisch wird.
            </p>
            <p>
              <strong>Merksatz:</strong> "Lenker zur Mitte" - dein Lenker sollte immer zur Straßenmitte zeigen.
            </p>
          </div>

          <h3>Gewöhnungsphase</h3>
          <ul>
            <li><strong>Tag 1-2:</strong> Volle Konzentration nötig, vor jeder Kreuzung nachdenken</li>
            <li><strong>Tag 3-4:</strong> Wird automatischer, aber noch bewusste Kontrolle</li>
            <li><strong>Ab Tag 5:</strong> Meiste Situationen automatisch</li>
            <li><strong>Risiko:</strong> Nach Pausen (Mittagsrast) kurz wieder fokussieren!</li>
          </ul>
        </section>

        <section>
          <h2>2. Kreisverkehr (Roundabouts)</h2>
          
          <h3>Grundregeln Kreisverkehr UK</h3>
          <div className="info-box">
            <h4><RotateCw size={20} /> Im Uhrzeigersinn!</h4>
            <ul>
              <li><strong>Richtung:</strong> Im Uhrzeigersinn (für uns "falsch herum")</li>
              <li><strong>Vorfahrt:</strong> Wer IM Kreisel ist, hat Vorfahrt</li>
              <li><strong>Einfahren:</strong> Von links schauen (Verkehr kommt von rechts!)</li>
              <li><strong>Position:</strong> Linke Spur für geradeaus/links, rechte für rechts</li>
            </ul>
          </div>

          <h3>Schritt für Schritt</h3>
          <ol>
            <li><strong>Annähern:</strong> Geschwindigkeit reduzieren, nach RECHTS schauen</li>
            <li><strong>Einordnen:</strong> 
              - 1. Ausfahrt (links): Linke Spur
              - 2. Ausfahrt (geradeaus): Beide Spuren möglich
              - 3.+ Ausfahrt (rechts): Rechte Spur
            </li>
            <li><strong>Einfahren:</strong> Wenn frei (Vorfahrt beachten!), im Uhrzeigersinn</li>
            <li><strong>Im Kreisel:</strong> Linke Spur halten, außer du nimmst nächste Ausfahrt</li>
            <li><strong>Ausfahrt:</strong> Blinker rechts setzen BEVOR du abbiegst</li>
          </ol>

          <h3>Mini-Roundabouts</h3>
          <p>
            Kleine Kreisverkehre (oft nur weiße Markierung). Gleiche Regeln, aber:
          </p>
          <ul>
            <li>Schnellerer Verkehr (weniger Platz zum Bremsen)</li>
            <li>Radfahrer können gerade über die Markierung fahren</li>
            <li>Vorsicht: Autos nehmen manchmal keine Rücksicht</li>
          </ul>

          <div className="warning-box">
            <AlertTriangle size={20} />
            <strong>Für Radfahrer:</strong> In großen, mehrspurigen Kreisverkehren kannst du auch 
            AUSSEN bleiben (rechte Spur) und bei jeder Ausfahrt Vorfahrt gewähren. Sicherer, 
            aber langsamer als im Kreis mitzufahren.
          </div>
        </section>

        <section>
          <h2>3. Vorfahrtsregeln</h2>
          
          <h3>Es gibt KEINE "Rechts vor Links" Regel in UK!</h3>
          <p>
            Im Gegensatz zu Deutschland/Schweiz/Österreich gilt in UK:
          </p>
          <ul>
            <li><strong>Vorfahrt nur durch Schilder geregelt</strong></li>
            <li><strong>Ohne Schilder:</strong> Höflichkeit & Kommunikation (Handzeichen, Augenkontakt)</li>
            <li><strong>Hauptstraße hat Vorfahrt</strong> (durch Schilder erkennbar)</li>
          </ul>

          <h3>Wichtige Schilder</h3>
          <div className="data-grid">
            <div className="data-item">
              <strong>STOP:</strong> Vollständig anhalten, auch ohne Verkehr
            </div>
            <div className="data-item">
              <strong>GIVE WAY:</strong> Dreieck nach unten - Vorfahrt gewähren
            </div>
            <div className="data-item">
              <strong>Priority Road:</strong> Gelbe Raute - Du hast Vorfahrt
            </div>
            <div className="data-item">
              <strong>No Entry:</strong> Roter Kreis, weißer Balken - Einfahrt verboten
            </div>
          </div>

          <h3>Zebrastreifen (Pedestrian Crossings)</h3>
          <ul>
            <li><strong>Zebra Crossing:</strong> Fußgänger haben absolute Vorfahrt</li>
            <li><strong>Pelican Crossing:</strong> Mit Ampel (Fußgänger drücken Knopf)</li>
            <li><strong>Puffin Crossing:</strong> Sensoren erkennen Fußgänger</li>
            <li><strong>Toucan Crossing:</strong> Für Fußgänger UND Radfahrer</li>
          </ul>

          <div className="info-box">
            <h4>🚶 Zebrastreifen-Kultur UK</h4>
            <p>
              In UK wird die Vorfahrt an Zebrastreifen <strong>sehr ernst genommen</strong>. 
              Autos halten immer an. Als Radfahrer: Du MUSST auch anhalten!
            </p>
          </div>
        </section>

        <section>
          <h2>4. Handzeichen & Kommunikation</h2>
          
          <h3>Pflicht-Handzeichen</h3>
          <div className="checklist">
            <div className="check-item">
              <strong>Rechts abbiegen:</strong> Rechter Arm ausgestreckt (Handfläche nach vorne)
            </div>
            <div className="check-item">
              <strong>Links abbiegen:</strong> Linker Arm ausgestreckt (Handfläche nach vorne)
            </div>
            <div className="check-item">
              <strong>Stoppen:</strong> Rechter Arm angewinkelt, Handfläche nach unten (Auf-Ab-Bewegung)
            </div>
          </div>

          <h3>Empfohlene Zeichen</h3>
          <ul>
            <li><strong>Danke:</strong> Hand heben oder nicken (nach Vorfahrt gewähren)</li>
            <li><strong>Entschuldigung:</strong> Hand heben (bei Fehler)</li>
            <li><strong>Hinderniss:</strong> Mit Hand auf Schlagloch/Gefahr zeigen (bei Gruppe)</li>
          </ul>

          <h3>Augenkontakt</h3>
          <p>
            In UK ist <strong>Augenkontakt mit Autofahrern</strong> sehr wichtig:
          </p>
          <ul>
            <li>Vor Einfahren in Kreisverkehr</li>
            <li>An Kreuzungen</li>
            <li>Beim Überholen (Schulterblick!)</li>
            <li>An Single-Track Road Passstellen</li>
          </ul>
        </section>

        <section>
          <h2>5. Beleuchtung & Sichtbarkeit</h2>
          
          <h3>Gesetzliche Pflicht</h3>
          <div className="warning-box">
            <AlertTriangle size={20} />
            <strong>Zwischen Sonnenuntergang und Sonnenaufgang PFLICHT:</strong>
            <ul>
              <li>Weißes Frontlicht (vorne)</li>
              <li>Rotes Rücklicht (hinten)</li>
              <li>Beide müssen funktionieren (Geldstrafe bei Kontrolle!)</li>
            </ul>
          </div>

          <h3>Licht-Anforderungen</h3>
          <ul>
            <li><strong>Frontlicht:</strong> Weiß, nach vorne gerichtet</li>
            <li><strong>Rücklicht:</strong> Rot, nach hinten gerichtet</li>
            <li><strong>Blinklicht:</strong> Erlaubt (50-240 Blitze/Minute)</li>
            <li><strong>Dauerlicht:</strong> Auch erlaubt (oft besser sichtbar)</li>
            <li><strong>Helligkeit:</strong> Keine Mindestanforderung (aber: Sichtbar sein!)</li>
          </ul>

          <h3>Reflektoren</h3>
          <p>Zusätzlich empfohlen (nicht Pflicht):</p>
          <ul>
            <li>Gelbe Speichenreflektoren (vorne & hinten)</li>
            <li>Weiße Reflektoren (vorne)</li>
            <li>Rote Reflektoren (hinten)</li>
            <li>Pedalreflektoren</li>
          </ul>

          <div className="pro-tip">
            <h3>💡 Schottland-Spezial: Licht auch tagsüber!</h3>
            <p>
              Aufgrund von Nebel, tiefen Wolken und früher Dämmerung empfehlen wir 
              <strong> Licht auch tagsüber einzuschalten</strong> - besonders in den Highlands!
            </p>
          </div>
        </section>

        <section>
          <h2>6. Straßentypen & wo du fahren darfst</h2>
          
          <h3>Motorways (M-Straßen)</h3>
          <div className="warning-box">
            <AlertTriangle size={20} />
            <strong>VERBOTEN für Radfahrer!</strong><br />
            M1, M4, M8, M74, M80, M90 etc. - alle M-Straßen sind tabu.
          </div>

          <h3>A-Roads (Hauptstraßen)</h3>
          <ul>
            <li><strong>Erlaubt:</strong> Ja, Radfahrer dürfen A-Roads nutzen</li>
            <li><strong>Aber:</strong> Oft stark befahren, schneller Verkehr</li>
            <li><strong>Empfehlung:</strong> Wenn möglich B-Roads oder unclassified roads wählen</li>
            <li><strong>A-Roads in Highlands:</strong> Oft wenig Verkehr, OK zu fahren</li>
          </ul>

          <h3>B-Roads (Nebenstraßen)</h3>
          <ul>
            <li>Ideal für Radfahrer</li>
            <li>Weniger Verkehr</li>
            <li>Oft landschaftlich schöner</li>
            <li>Manchmal schmalere Straßen (Vorsicht bei Gegenverkehr)</li>
          </ul>

          <h3>Single-Track Roads</h3>
          <p>
            Einspurige Straßen mit Passstellen - typisch für Highlands & Islands.
          </p>
          <div className="info-box">
            <h4>🛣️ Single-Track Rules</h4>
            <ul>
              <li><strong>Passstellen:</strong> Kleine Buchten zum Ausweichen (Passing Places)</li>
              <li><strong>Regel:</strong> Wer näher an der Passstelle ist, fährt vor</li>
              <li><strong>Für Radfahrer:</strong> Immer rechts ranfahren und Autos vorbeilassen</li>
              <li><strong>Nicht warten IN der Passstelle</strong> - davor oder dahinter!</li>
              <li><strong>Bedanken:</strong> Hand heben als Dank üblich</li>
            </ul>
          </div>

          <h3>Cycle Paths & Lanes</h3>
          <ul>
            <li><strong>Cycle Lane:</strong> Markierte Radspur auf der Straße (gestrichelte Linie)</li>
            <li><strong>Mandatory Cycle Lane:</strong> Durchgezogene Linie (selten)</li>
            <li><strong>Cycle Path:</strong> Baulich getrennt von der Straße</li>
            <li><strong>Shared Use Path:</strong> Für Fußgänger & Radfahrer (Rücksicht!)</li>
          </ul>

          <h3>Keine Radwegpflicht!</h3>
          <p>
            In UK gibt es <strong>keine Radwegpflicht</strong>. Auch bei vorhandenem Radweg 
            darfst du die Straße nutzen.
          </p>
        </section>

        <section>
          <h2>7. Überholen & Überholt werden</h2>
          
          <h3>Highway Code Rule 163</h3>
          <p>
            Seit 2022 verschärfte Regeln zum Schutz von Radfahrern:
          </p>
          <ul>
            <li><strong>Mindestabstand:</strong> 1.5m bei unter 30 mph (48 km/h)</li>
            <li><strong>Bei höherer Geschwindigkeit:</strong> Mindestens 2m Abstand</li>
            <li><strong>Bei engen Straßen:</strong> Warten bis sicher überholt werden kann</li>
            <li><strong>Gegenverkehr:</strong> Nicht zwischen entgegenkommendem Auto & Radfahrer durchquetschen</li>
          </ul>

          <h3>Als Radfahrer überholen</h3>
          <ul>
            <li><strong>Rechts überholen:</strong> Andere Radfahrer, langsame Fahrzeuge</li>
            <li><strong>Schulterblick:</strong> Vor jedem Spurwechsel</li>
            <li><strong>Handzeichen:</strong> Vor Spurwechsel rechten Arm ausstrecken</li>
            <li><strong>Genug Platz:</strong> Mind. 1m Abstand zu überholtem Objekt</li>
          </ul>

          <h3>In Gruppen fahren</h3>
          <ul>
            <li><strong>Hintereinander:</strong> Auf schmalen Straßen</li>
            <li><strong>Nebeneinander:</strong> Auf breiten Straßen erlaubt (max. 2 Personen breit)</li>
            <li><strong>Single file:</strong> Bei entgegenkommendem Verkehr</li>
            <li><strong>Kommunikation:</strong> Handzeichen für Hindernisse weitergeben</li>
          </ul>
        </section>

        <section>
          <h2>8. Alkohol am Steuer... äh, Lenker</h2>
          
          <h3>Gesetzliche Lage</h3>
          <p>
            In UK gibt es <strong>keine festgelegte Promillegrenze für Radfahrer</strong>.
          </p>
          <ul>
            <li><strong>§ 30 Road Traffic Act:</strong> "Drunk in charge of a carriage" (max. £1000 Strafe)</li>
            <li><strong>Aber:</strong> Wird selten angewendet</li>
            <li><strong>Praxis:</strong> Polizei greift nur bei Gefährdung ein</li>
          </ul>

          <div className="warning-box">
            <AlertTriangle size={20} />
            <strong>Unsere Empfehlung:</strong><br />
            Auch wenn es rechtlich "lockerer" ist: <strong>Nicht betrunken Rad fahren!</strong> 
            Das Unfallrisiko ist enorm hoch, besonders bei Linksverkehr, den du nicht gewohnt bist.
          </div>

          <h3>Whisky-Destillerie Touren</h3>
          <p>
            Bei Destillerie-Besuchen mit Verkostung:
          </p>
          <ul>
            <li>Nur kleine Schlucke probieren (Spit & Spit)</li>
            <li>Lange Pausen zwischen Distillery-Visits einlegen</li>
            <li>Viel Wasser trinken</li>
            <li>Bei mehreren Verkostungen: Übernachtung in der Nähe planen</li>
          </ul>
        </section>

        <section>
          <h2>9. Smartphones & Ablenkung</h2>
          
          <h3>Handy am Lenker</h3>
          <ul>
            <li><strong>Telefonieren:</strong> Nicht verboten, aber gefährlich</li>
            <li><strong>Navigation:</strong> Erlaubt (Halterung verwenden!)</li>
            <li><strong>Kopfhörer:</strong> Erlaubt, aber nicht empfohlen (Verkehr nicht hören)</li>
            <li><strong>Empfehlung:</strong> Zum Telefonieren/Texten anhalten</li>
          </ul>

          <h3>Navigation während der Fahrt</h3>
          <ul>
            <li>Handy-Halterung am Lenker (wasserdicht!)</li>
            <li>Sprachansagen aktivieren</li>
            <li>Offline-Karten (Mobilfunk nicht überall)</li>
            <li>Route vor Fahrt grob ansehen</li>
          </ul>
        </section>

        <section>
          <h2>10. Besondere Situationen</h2>
          
          <h3>Schafe & Vieh auf der Straße</h3>
          <ul>
            <li><strong>Langsam fahren</strong> (Tiere können plötzlich laufen)</li>
            <li><strong>Nicht hupen/klingeln</strong> (erschreckt Tiere)</li>
            <li><strong>Abstand halten</strong> (besonders zu Highland Cattle - können aggressiv sein)</li>
            <li><strong>Geduld haben</strong> (Schafherde kann Straße blockieren)</li>
          </ul>

          <h3>Cattle Grids (Viehgitter)</h3>
          <p>
            Metallgitter über der Straße (hält Vieh von Straßen ab).
          </p>
          <ul>
            <li><strong>Langsam überqueren</strong> (rutschig, besonders bei Nässe)</li>
            <li><strong>Gerade Linie</strong> (nicht im Winkel)</li>
            <li><strong>Alternative:</strong> Oft gibt es seitliche Pforte für Fußgänger/Radfahrer</li>
          </ul>

          <h3>Fords (Wasserdurchfahrten)</h3>
          <ul>
            <li>Vor Durchfahrt Tiefe prüfen</li>
            <li>Nach Regen meiden (kann tief & reißend sein)</li>
            <li>Langsam durchfahren (Spritzwasser)</li>
            <li>Danach Bremsen testen (können nass sein)</li>
          </ul>

          <h3>Baustellen</h3>
          <ul>
            <li><strong>Temporary Traffic Lights:</strong> Beachten! (auch für Radfahrer)</li>
            <li><strong>Road Closed:</strong> Manchmal nur für Autos - fragen ob Radfahrer durch dürfen</li>
            <li><strong>Diversion (Umleitung):</strong> Folgen, außer Radfahrer-Hinweis existiert</li>
          </ul>
        </section>

        <div className="pro-tip">
          <h3>🎯 Die 10 wichtigsten Verkehrsregeln für Schottland</h3>
          <ol>
            <li><strong>LINKS fahren</strong> (immer, auch auf leeren Straßen!)</li>
            <li><strong>Nach RECHTS schauen</strong> zuerst (Verkehr kommt von rechts)</li>
            <li><strong>Kreisverkehr im Uhrzeigersinn</strong> (wer drin ist, hat Vorfahrt)</li>
            <li><strong>Keine "Rechts vor Links"</strong> (Schilder beachten!)</li>
            <li><strong>Zebrastreifen = absolute Vorfahrt</strong> für Fußgänger</li>
            <li><strong>Licht bei Dunkelheit</strong> Pflicht (auch tagsüber empfohlen)</li>
            <li><strong>Handzeichen geben</strong> vor Abbiegen</li>
            <li><strong>Single-Track Roads:</strong> Rechts ran, Autos vorbeilassen</li>
            <li><strong>M-Straßen verboten</strong> für Radfahrer</li>
            <li><strong>Danke sagen:</strong> Hand heben (UK-Höflichkeit)</li>
          </ol>
        </div>

        <div className="info-box">
          <h4>📖 Highway Code</h4>
          <p>
            Der offizielle Highway Code (Straßenverkehrsordnung UK) ist online kostenlos verfügbar:
          </p>
          <p>
            <strong>gov.uk/guidance/the-highway-code</strong>
          </p>
          <p>
            Besonders relevant für Radfahrer: <strong>Rules 59-82</strong> (Rules for Cyclists)
          </p>
        </div>

        <div className="download-section">
          <h3>📥 Verkehrsregeln-Guide herunterladen</h3>
          <button className="download-btn" onClick={() => window.print()}>
            Verkehrsregeln-Guide als PDF speichern
          </button>
        </div>
      </article>
    </div>
  );
}
